// Components
import { Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, SearchBox } from "@fluentui/react-components";
// Styles
import { useStyles } from "./ContextualMenu.styles";
// Utilities
import * as React from "react";
import { customizeStyles } from "../utilities/StyleUtilities";
/** Key prefix for menu dividers */ export const MENU_DIVIDER_KEY = "MENU_DIVIDER_KEY";
/**
 * Renders a single menu item or divider
 * @param item The menu item props
 * @param index The item index
 * @param styles The component styles
 * @param closeMenu Callback to close the menu
 * @returns A MenuItem or MenuDivider component
 */ const renderMenuItem = (item, index, styles, closeMenu)=>{
    const { content, dataTestId, noHover, onClick, ...itemProps } = item;
    const renderedContent = typeof content === "function" ? content(closeMenu) : content;
    const handleClick = (event)=>{
        onClick?.(event);
    };
    const handleKeyDown = (event)=>{
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onClick?.(event);
            closeMenu();
        }
    };
    return String(item.key).includes(MENU_DIVIDER_KEY) ? /*#__PURE__*/ React.createElement(MenuDivider, {
        ...itemProps,
        className: styles.menuDivider
    }) : /*#__PURE__*/ React.createElement(MenuItem, {
        key: item.key ?? item.id ?? index,
        className: noHover ? styles.menuItemNoHover : styles.menuItem,
        ...itemProps,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        "data-testid": dataTestId ?? itemProps["data-testid"]
    }, renderedContent);
};
/**
 * ContextualMenu control that customizes the Fluent control to our own purposes
 * Follows Fluent UI recommendation of maximum 2 levels of nesting
 */ export const ContextualMenu = (props)=>{
    const { trigger, menuItems, customStyles, searchProps, dataTestId, ...menuProps } = props;
    const defaultStyles = useStyles();
    const styles = customizeStyles(defaultStyles, customStyles);
    // When no trigger, menu should be open by default
    const [internalOpen, setInternalOpen] = React.useState(!trigger);
    const isControlled = menuProps.open !== undefined;
    const open = isControlled ? menuProps.open : internalOpen;
    const closeMenu = ()=>{
        setInternalOpen(false);
    };
    const handleOpenChange = (e, data)=>{
        setInternalOpen(data.open);
        menuProps.onOpenChange?.(e, data);
    };
    const popover = /*#__PURE__*/ React.createElement(MenuPopover, {
        className: styles.root,
        "data-testid": dataTestId
    }, /*#__PURE__*/ React.createElement(MenuList, {
        className: styles.menuList
    }, searchProps && /*#__PURE__*/ React.createElement(SearchBox, {
        "data-testid": "contextual-menu-search-box",
        appearance: "filled-lighter",
        className: defaultStyles.searchContainer,
        placeholder: searchProps.placeholder,
        value: searchProps.value,
        onChange: searchProps.onChange
    }), menuItems.map((item, index)=>{
        const { submenuItems } = item;
        // If no submenu items, render a simple MenuItem
        if (!submenuItems || submenuItems.length === 0) {
            return renderMenuItem(item, index, styles, closeMenu);
        }
        // Render submenu
        return /*#__PURE__*/ React.createElement(Menu, {
            key: item.key ?? item.id ?? index
        }, /*#__PURE__*/ React.createElement(MenuTrigger, {
            disableButtonEnhancement: true
        }, renderMenuItem(item, index, styles, closeMenu)), /*#__PURE__*/ React.createElement(MenuPopover, {
            "data-testid": "contextual-submenu",
            className: styles.root
        }, /*#__PURE__*/ React.createElement(MenuList, {
            className: styles.menuList
        }, submenuItems.map((subItem, subIndex)=>renderMenuItem(subItem, subIndex, styles, closeMenu)))));
    })));
    return trigger ? /*#__PURE__*/ React.createElement(Menu, {
        ...menuProps,
        open: open,
        onOpenChange: handleOpenChange,
        closeOnScroll: true
    }, /*#__PURE__*/ React.createElement(MenuTrigger, {
        disableButtonEnhancement: true
    }, trigger), popover) : /*#__PURE__*/ React.createElement(Menu, {
        ...menuProps,
        open: open,
        onOpenChange: handleOpenChange,
        closeOnScroll: true
    }, popover);
};
